import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import type {
  ChildRow,
  ManifestColumn,
  ManifestObject,
} from "@shared-core/domain/object-ui/contract";
import {
  useCollectionCommands,
  useObjectList,
  useOwnKindFirstRow,
  useQueueCounts,
} from "@shared-core/domain/object-ui/hooks";
import {
  chosenValues,
  isOwnKindOnly,
  KIND_PARAM,
  leftOutNote,
  linkField,
  manyChosenLabel,
  ownKindQuery,
  rowTarget,
  shownFilters,
  shownFilterValue,
  withDefaultedFilter,
} from "@shared-core/domain/object-ui/list-includes";
import { entityFilterTarget } from "@shared-core/domain/object-ui/entity-filter";
import {
  idColumnFields,
  isWrappingColumn,
  visibleLinkField,
} from "@shared-core/domain/object-ui/list-columns";
import {
  formatListQuery,
  isListNarrowed,
  narrowingCount,
  parseListQuery,
  withFilter,
  withNarrowingCleared,
  withPage,
  withAllRows,
  withQueue,
  withSearch,
  withSort,
  type ListQuery,
} from "@shared-core/domain/object-ui/list-query";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { prefillParamsOf } from "@shared-core/domain/object-ui/related-prefill";
import { refsOf } from "@shared-core/domain/object-ui/refs";
import {
  parseFormattedText,
  plainTextOf,
} from "@shared-core/domain/formatted-text";
import {
  formatColumnValue,
  linkHref,
  stateToneOf,
} from "@shared-core/domain/object-ui/values";
import {
  cellFilesOf,
  detailOf,
  shownColumnOf,
} from "@shared-core/domain/object-ui/record";
import { workThroughSearch } from "@shared-core/domain/object-ui/work-through";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { BULK_TARGETS_FIELD } from "@shared-core/domain/object-ui/contract";
import { ErrorPanel } from "../../app/components/patterns/ErrorPanel";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { ObjectAbout } from "../../app/components/patterns/ObjectAbout";
import { DetailValue } from "../../app/components/patterns/DetailValue";
import { FileLinks } from "../../app/components/patterns/FileLinks";
import { RefLinks } from "../../app/components/patterns/RefLinks";
import { StateBadge } from "../../app/components/patterns/StateBadge";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Checkbox } from "../../app/components/primitives/checkbox";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../app/components/primitives/sheet";
import { Skeleton } from "../../app/components/primitives/skeleton";
import { cn } from "../../app/components/primitives/utils";
import { StackedRows } from "../../app/components/patterns/StackedRows";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { NotFoundPage } from "../NotFoundPage";
import {
  collectionCommandOpening,
  useCommandHost,
} from "../../app/useCommandHost";
import { useIsPhone } from "../../app/useIsPhone";
import { EntityFilter } from "./EntityFilter";
import { ObjectSwitchedOffPage } from "./ObjectSwitchedOffPage";

const ANY = "__any__";

/** A queue, or a yes/no filter, pressed or not (the mock-up's chips). */
/** A cell's text. Formatted text reads without its markers (a list shows a line of it, never the blocks). */
function listTextOf(item: ChildRow, declared: ManifestColumn): string {
  const column = shownColumnOf(declared, item);
  const value = formatColumnValue(item[column.field], column);
  return column.kind === "formatted_text" &&
    typeof item[column.field] === "string"
    ? plainTextOf(parseFormattedText(value))
    : value;
}

function Chip({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? "h-8 shrink-0 rounded-full border border-foreground bg-foreground px-3 text-sm text-background max-sm:h-10"
          : "h-8 shrink-0 rounded-full border border-border bg-card px-3 text-sm hover:bg-accent max-sm:h-10"
      }
    >
      {label}
      {count !== undefined && (
        <span className="ml-1.5 tabular-nums opacity-80">{count}</span>
      )}
    </button>
  );
}

/** The one line above the rows while a queue or a One-only filter is leaving the second object's rows out. */
function LeftOutNote({ note }: { note: string | null }) {
  return note ? (
    <p role="status" className="text-sm text-muted-foreground">
      {note}
    </p>
  ) : null;
}

/**
 * The list template (04_frontend_spec.md §6.5), the same for every object:
 * the queues the module declares (with counts), search and the declared
 * filters, the declared columns, paging, *Work through these*, the object's
 * create commands and its bulk commands over ticked rows. Everything runs
 * server-side; the URL holds the list's state.
 */
export function ObjectListPage() {
  const { objectId = "" } = useParams();
  const index = useObjectManifest();
  if (!index.isObjectKnown(objectId) || !index.objectOf(objectId).list)
    return <NotFoundPage />;
  if (!index.isObjectEnabled(objectId))
    return <ObjectSwitchedOffPage object={index.objectOf(objectId)} />;
  return <ObjectList key={objectId} object={index.objectOf(objectId)} />;
}

function ObjectList({ object }: { object: ManifestObject }) {
  const list = object.list!;
  const { search } = useLocation();
  const navigate = useNavigate();
  const index = useObjectManifest();
  const query = parseListQuery(search, list);
  const read = useObjectList(object, query);
  const counts = useQueueCounts(object);
  const collection = useCollectionCommands(object);
  const bulkCommands = index.bulkCommandsOf(object.id);
  const { openCommand, shell } = useCommandHost();
  const isPhone = useIsPhone();
  const [selected, setSelected] = useState<string[]>([]);
  const includes = list.includes ?? null;
  const includedObject = includes ? index.objectOf(includes.object) : null;
  // Switched off for the person: the list answers its own rows, and draws neither Kind nor the filters only the second
  // object's rows have.
  const isIncludedEnabled = includes
    ? index.isObjectEnabled(includes.object)
    : false;
  const isOnlyOwnKind = isOwnKindOnly(list, query);
  const walksOwnRows = !!includes && isIncludedEnabled && !isOnlyOwnKind;
  const ownFirst = useOwnKindFirstRow(object, query, walksOwnRows);
  const [text, setText] = useState(query.q ?? "");
  const debouncedText = useDebouncedValue(text, 300);

  const go = (next: ListQuery) => {
    setSelected([]);
    navigate(
      paths.objectList({
        objectId: object.id,
        search: formatListQuery(next, list),
      }),
    );
  };

  useEffect(() => {
    if ((query.q ?? "") !== debouncedText.trim())
      go(withSearch(query, debouncedText));
    // Only the typed text drives this; the query is read fresh each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);

  const keyOf = (item: ChildRow) => rowTarget(index, object.id, list, item);
  const hasRecords = !!object.record;
  /** The row's own record page; a row of the list's second object opens that object's. */
  const hrefOf = (item: ChildRow) => {
    const target = keyOf(item);
    return index.objectOf(target.object).record && target.key
      ? paths.objectRecord({ objectId: target.object, recordKey: target.key })
      : null;
  };
  const rowKeyOf = (item: ChildRow) => {
    const target = keyOf(item);
    return `${target.object}/${target.key}`;
  };
  const keyColumn = list.columns.find((c) => c.field === list.key_field);
  const kindColumn = includes
    ? list.columns.find((c) => c.field === KIND_PARAM)
    : undefined;
  const page = read.status === "ready" ? read.value : null;
  // An internal id (a uuid, `aud_…`) is not drawn as a column: the row links on its name instead.
  const idFields = idColumnFields(
    list,
    object.identifier.kind,
    page?.items ?? [],
  );
  const columns = list.columns.filter((c) => !idFields.has(c.field));
  const linkFieldOf = (item: ChildRow) =>
    visibleLinkField(list, linkField(list, item), idFields);

  /** One column's value in a row: the row's number links its record, a state is a badge. */
  const cellOf = (
    item: ChildRow,
    declared: ManifestColumn,
    href: string | null,
  ): ReactNode => {
    const value = listTextOf(item, declared);
    // The column the cell draws: the declared one, or its fallback while the declared field is empty.
    const column = shownColumnOf(declared, item);
    if (declared.field === linkFieldOf(item) && href)
      return (
        <Link to={href} onClick={(e) => e.stopPropagation()}>
          {value}
        </Link>
      );
    if (column.kind === "state" && item[column.field] != null)
      return (
        <StateBadge
          tone={stateToneOf(
            index.objectOf(rowTarget(index, object.id, list, item).object),
            item[column.field],
          )}
        >
          {value}
        </StateBadge>
      );
    const external =
      column.kind === "link" ? linkHref(item[column.field]) : null;
    if (external)
      return (
        <a
          href={external}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {column.link_text ?? declared.label}
        </a>
      );
    // Records the cell names (a purchase order's order) link to their own page, not to the row's.
    const refs = refsOf(index, column, item);
    if (refs) return <RefLinks refs={refs} />;
    // A file opens itself (with the person's sign-in), not the row's record.
    const files = cellFilesOf(column, item);
    if (files) return <FileLinks files={files} />;
    // A second field the column shows on hover (`cis_field(detail=...)`); a phone writes it after the value.
    const detail = detailOf(declared, item);
    if (detail)
      return <DetailValue value={value} detail={detail} inline={isPhone} />;
    return value;
  };

  /** A tick box on a row of the list's own object only: a bulk command never receives the second object's key. */
  const selectBox = (key: string) => (
    <Checkbox
      aria-label={`Select ${key}`}
      checked={selected.includes(key)}
      onCheckedChange={(checked) =>
        setSelected((current) =>
          checked === true
            ? [...current, key]
            : current.filter((k) => k !== key),
        )
      }
    />
  );
  const activeQueue = list.queues.find((q) => q.id === query.queue) ?? null;
  const hasFilters = Object.keys(query.filters).length > 0 || !!query.q;

  const emptyMessage = activeQueue
    ? "Nothing in this queue."
    : hasFilters
      ? `No ${object.plural.toLowerCase()} match.`
      : `No ${object.plural.toLowerCase()} yet.`;

  /**
   * "Work through these": the list's own records, one at a time. On a list that also holds a second object and shows
   * both, it walks the own rows alone (the second object's rows are not records of this object).
   */
  const walk = walksOwnRows
    ? ownFirst.status === "ready"
      ? {
          read: ownFirst.value,
          query: ownKindQuery(list, query) ?? query,
          at: 1,
        }
      : null
    : page
      ? {
          read: page,
          query,
          at: (page.page - 1) * page.page_size + 1,
        }
      : null;
  const firstToWalk = walk?.read.items[0];
  // Only a queue, a filter or a search makes a set to step through; the whole object is chosen from, not walked.
  const workThrough =
    hasRecords &&
    isListNarrowed(query) &&
    walk &&
    firstToWalk &&
    walk.read.total > 1
      ? {
          total: walk.read.total,
          href: paths.objectRecord({
            objectId: object.id,
            recordKey: rowTarget(index, object.id, list, firstToWalk).key,
            search: workThroughSearch({
              through: formatListQuery(withPage(walk.query, 1)),
              at: walk.at,
            }),
          }),
        }
      : null;

  const narrowing = narrowingCount(query);
  /** The declared filters, then the sort: inline on a desktop, in the Filters sheet on a phone. */
  const filterControls = (
    <>
      {shownFilters(list, isIncludedEnabled).map((filter) => {
        const current = shownFilterValue(filter, query);
        const hasDefault = filter.default != null;
        if (filter.kind === "boolean")
          return (
            <Chip
              key={filter.param}
              label={filter.label}
              active={current === "true"}
              onClick={() =>
                go(
                  hasDefault
                    ? withDefaultedFilter(
                        query,
                        filter,
                        current === "true" ? "false" : "true",
                      )
                    : withFilter(
                        query,
                        filter.param,
                        current === "true" ? null : "true",
                      ),
                )
              }
            />
          );
        if (filter.kind === "date")
          return (
            <label
              key={`${filter.param}:${typeof current === "string" ? current : ""}`}
              className="flex items-center gap-1.5 text-sm"
            >
              {filter.label}
              <Input
                type="date"
                className={isPhone ? "h-10 w-full" : "h-9 w-40"}
                defaultValue={typeof current === "string" ? current : ""}
                onChange={(e) =>
                  go(
                    withFilter(
                      query,
                      filter.param,
                      e.currentTarget.value || null,
                    ),
                  )
                }
              />
            </label>
          );
        const entityTarget = entityFilterTarget(index, filter);
        if (entityTarget)
          return (
            <EntityFilter
              key={filter.param}
              list={list}
              filter={filter}
              target={entityTarget}
              value={typeof current === "string" ? current : null}
              rows={page?.items ?? null}
              className={isPhone ? "h-10 w-full" : "h-9 w-56"}
              onChange={(value) => go(withFilter(query, filter.param, value))}
            />
          );
        // A filter naming an object the person can't list from (no list yet, or switched off) is typed instead.
        if (filter.kind === "text" || filter.kind === "entity")
          return (
            <Input
              key={`${filter.param}:${typeof current === "string" ? current : ""}`}
              aria-label={filter.label}
              placeholder={filter.label}
              className={isPhone ? "h-10 w-full" : "h-9 w-40"}
              defaultValue={typeof current === "string" ? current : ""}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  go(
                    withFilter(
                      query,
                      filter.param,
                      e.currentTarget.value.trim() || null,
                    ),
                  );
              }}
            />
          );
        if (filter.kind !== "enum" && filter.kind !== "multi_enum") return null;
        // A many-valued filter with no fixed options (the customers or suppliers a link narrows to) has nothing to pick from.
        if (filter.kind === "multi_enum" && filter.options.length === 0)
          return null;
        const isKind = !!includes && filter.param === KIND_PARAM;
        // A many-valued filter holds a list even for one value (`route=a` reads back as ["a"]).
        const chosen = chosenValues(current);
        const many = manyChosenLabel(filter, current);
        return (
          <Select
            key={filter.param}
            value={chosen[0] ?? ANY}
            onValueChange={(value) =>
              go(
                hasDefault
                  ? withDefaultedFilter(query, filter, value)
                  : withFilter(
                      query,
                      filter.param,
                      value === ANY ? null : value,
                    ),
              )
            }
          >
            <SelectTrigger
              aria-label={filter.label}
              className={isPhone ? "h-10 w-full" : "h-9 w-auto min-w-36"}
            >
              {many ? <SelectValue>{many}</SelectValue> : <SelectValue />}
            </SelectTrigger>
            <SelectContent>
              {!hasDefault && (
                <SelectItem value={ANY}>
                  {isKind ? `${filter.label}: all` : `${filter.label}: any`}
                </SelectItem>
              )}
              {filter.options.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {hasDefault ? `${filter.label}: ${label}` : label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      })}
      {list.sorts.length > 0 && (
        <Select
          value={query.sort ?? ANY}
          onValueChange={(value) =>
            go(withSort(query, value === ANY ? null : value))
          }
        >
          <SelectTrigger
            aria-label="Sort"
            className={isPhone ? "h-10 w-full" : "h-9 w-auto min-w-36"}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Sort: default</SelectItem>
            {list.sorts.flatMap(([field, label]) => [
              <SelectItem key={field} value={field}>
                {label} ↑
              </SelectItem>,
              <SelectItem key={`-${field}`} value={`-${field}`}>
                {label} ↓
              </SelectItem>,
            ])}
          </SelectContent>
        </Select>
      )}
    </>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold">{object.plural}</h1>
          <ObjectAbout about={object.about} plural={object.plural} />
        </div>
        {collection.status === "ready" && collection.value.length > 0 && (
          <div className="flex flex-wrap items-start gap-2">
            {collection.value.map((command, position) => (
              <div key={command.name} className="flex flex-col gap-0.5">
                {/* The list's first create is its primary button; the others are outline (Ben, 25 Oct). */}
                <Button
                  variant={position === 0 ? "default" : "outline"}
                  size="sm"
                  className="max-sm:h-10"
                  disabled={!command.enabled}
                  title={command.reason ?? undefined}
                  onClick={() =>
                    openCommand(
                      collectionCommandOpening(index, command, {
                        context: object.plural,
                        params: prefillParamsOf(query),
                      }),
                    )
                  }
                >
                  {command.label}
                </Button>
                {/* A phone has no hover to show the title. */}
                {isPhone && !command.enabled && command.reason && (
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    {command.reason}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {list.queues.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Queues">
          <Chip
            label="All"
            active={!activeQueue && !hasFilters}
            onClick={() => go(withAllRows(query, list))}
          />
          {list.queues.map((queue) => {
            const count = counts[queue.id];
            return (
              <Chip
                key={queue.id}
                label={queue.label}
                active={activeQueue?.id === queue.id}
                {...(queue.count && count != null ? { count } : {})}
                onClick={() => go(withQueue(query, queue))}
              />
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {list.searchable && (
          <Input
            type="search"
            aria-label={`Search ${object.plural.toLowerCase()}`}
            placeholder={`Search ${object.plural.toLowerCase()}`}
            className="w-full sm:max-w-xs"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        )}
        {isPhone
          ? shownFilters(list, isIncludedEnabled).length + list.sorts.length >
              0 && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-10 shrink-0">
                    {narrowing > 0 ? `Filters · ${narrowing}` : "Filters"}
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="max-h-[85dvh] overflow-y-auto"
                >
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription className="sr-only">
                      Narrow the {object.plural.toLowerCase()} shown.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="flex flex-wrap gap-2 px-4">
                    {filterControls}
                  </div>
                  <SheetFooter>
                    <Button
                      variant="outline"
                      className="h-10"
                      disabled={narrowing === 0}
                      onClick={() => go(withNarrowingCleared(query))}
                    >
                      Clear
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            )
          : filterControls}
      </div>

      {includedObject && isIncludedEnabled && (
        <LeftOutNote note={leftOutNote(list, query, includedObject.plural)} />
      )}

      {bulkCommands.length > 0 && selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span>{selected.length} selected</span>
          {bulkCommands.map((command) => (
            <Button
              key={command.id}
              size="sm"
              variant="outline"
              onClick={() =>
                openCommand({
                  endpoint: {
                    command,
                    href: command.path,
                    dryRunHref: command.dry_run_path,
                  },
                  context: `${selected.length} ${object.plural.toLowerCase()}`,
                  initialBody: { [BULK_TARGETS_FIELD]: selected },
                })
              }
            >
              {command.label}
            </Button>
          ))}
          <button
            type="button"
            className="underline"
            onClick={() => setSelected([])}
          >
            Clear
          </button>
        </div>
      )}

      {read.status === "error" ? (
        read.httpStatus === 403 ? (
          <p className="text-sm text-muted-foreground">
            Your role can't open {object.plural.toLowerCase()}.
          </p>
        ) : (
          <ErrorPanel
            message={`Couldn't load ${object.plural.toLowerCase()}.`}
            onRetry={read.reload}
          />
        )
      ) : isPhone ? (
        <Card className="p-1">
          {!page ? (
            <div className="space-y-3 p-3">
              {[0, 1, 2].map((n) => (
                <Skeleton key={n} className="h-16 w-full" />
              ))}
            </div>
          ) : page.items.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">{emptyMessage}</p>
          ) : (
            <StackedRows
              ariaLabel={object.plural}
              rows={page.items.map((item) => {
                const target = keyOf(item);
                const href = hrefOf(item);
                const numberColumn = columns.find(
                  (c) => c.field === linkFieldOf(item),
                );
                const number = numberColumn
                  ? cellOf(item, numberColumn, href)
                  : target.key;
                return {
                  key: rowKeyOf(item),
                  // The kind badge sits beside the number on the card.
                  title: kindColumn ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {number}
                      {cellOf(item, kindColumn, href)}
                    </span>
                  ) : (
                    number
                  ),
                  ...(bulkCommands.length > 0 && !target.isIncluded
                    ? { leading: selectBox(target.key) }
                    : {}),
                  fields: columns
                    .filter(
                      (column) =>
                        column !== keyColumn &&
                        column !== kindColumn &&
                        column !== numberColumn &&
                        // A number the row's kind has none of ("Tharstern job number" on a One order) is left off.
                        !(
                          column.kind === "identifier" &&
                          item[column.field] == null
                        ),
                    )
                    .map((column) => ({
                      label: column.label,
                      value: cellOf(item, column, href),
                    })),
                  ...(href ? { onOpen: () => navigate(href) } : {}),
                };
              })}
            />
          )}
        </Card>
      ) : (
        <Card className="p-1">
          <Table>
            <TableHeader>
              <TableRow>
                {bulkCommands.length > 0 && (
                  <TableHead className="w-8">
                    <span className="sr-only">Select</span>
                  </TableHead>
                )}
                {columns.map((column) => (
                  <TableHead
                    key={column.field}
                    className={cn(
                      (column.kind === "number" || column.kind === "money") &&
                        "text-right",
                      "whitespace-normal",
                    )}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!page &&
                [0, 1, 2].map((n) => (
                  <TableRow key={n}>
                    <TableCell colSpan={columns.length + 1}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              {page?.items.map((item) => {
                const target = keyOf(item);
                const href = hrefOf(item);
                return (
                  <TableRow
                    key={rowKeyOf(item)}
                    className={href ? "cursor-pointer" : undefined}
                    onClick={href ? () => navigate(href) : undefined}
                  >
                    {bulkCommands.length > 0 && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {!target.isIncluded && selectBox(target.key)}
                      </TableCell>
                    )}
                    {columns.map((column) => {
                      const kind = column.kind;
                      const isKey = column.field === linkFieldOf(item);
                      const isWrapping = isWrappingColumn(column);
                      const cell = cellOf(item, column, href);
                      return (
                        <TableCell
                          key={column.field}
                          className={cn(
                            kind === "number" || kind === "money"
                              ? "text-right tabular-nums"
                              : isKey && "font-medium tabular-nums",
                            isWrapping && "whitespace-normal",
                          )}
                        >
                          {/* Text wraps within a width and stops at two lines, the whole of it on hover. The
                              list's name column keeps room to read; the rest may narrow before the list scrolls. */}
                          {isWrapping ? (
                            <div
                              className={cn(
                                "line-clamp-2 max-w-80 break-words",
                                column.field === list.title_field
                                  ? "min-w-40"
                                  : "min-w-14",
                              )}
                              title={listTextOf(item, column)}
                            >
                              {cell}
                            </div>
                          ) : (
                            cell
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {page && page.items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    className="text-sm text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {page && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {workThrough ? (
            <Button
              variant="outline"
              onClick={() => navigate(workThrough.href)}
            >
              Work through these ({workThrough.total}) →
            </Button>
          ) : (
            <span />
          )}
          <ListPagination
            page={page.page}
            totalPages={page.total_pages}
            onPageChange={(next) => go(withPage(query, next))}
          />
        </div>
      )}
      {shell}
    </div>
  );
}
